import { useState, useCallback, useEffect } from 'react';
import { creditService } from '../services/creditService';
import { mapCreditSummaryData, mapPeriodSummary, mapLinkedAccount } from '../utils/credits.utils';
import type { CreditSummary, Statement, PeriodSummary, LinkedAccount } from '../types/credits.types';
import { useAuthContext } from '@/features/auth/context/AuthContext/AuthContext';
import { useAsyncGuard } from '@chikara/hooks';
import { useSpinner, useToast } from '@chikara/ui';
import { t } from '@/i18n';

interface UseCreditsState {
  creditSummary: CreditSummary | null;
  periodSummary: PeriodSummary | null;
  linkedAccount: LinkedAccount | null;
  isLoading: boolean;
  error: string | null;
  isPaymentModalOpen: boolean;
  isConfirmingPayment: boolean;
  pagination: any;
  selectedMonth: string;
  selectedYear: string;
  statements: Statement[];
}

interface UseCreditsActions {
  handleMakePayment: () => void;
  handleClosePaymentModal: () => void;
  handleConfirmPayment: () => Promise<void>;
  handleDownloadStatement: (statement: Statement) => void;
  setSelectedMonth: (month: string) => void;
  setSelectedYear: (year: string) => void;
  handleSearch: () => void;
}

export function useCredits(): UseCreditsState & UseCreditsActions {
  const { payload: authPayload } = useAuthContext();
  const { show, hide, isSpinnerLoading: isLoading } = useSpinner();
  const { info } = useToast();

  const customer_uuid = authPayload?.customer_uuid;

  const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
  const currentYear = new Date().getFullYear().toString().slice(-2);

  const [creditSummary, setCreditSummary] = useState<CreditSummary | null>(null);
  const [periodSummary, setPeriodSummary] = useState<PeriodSummary | null>(null);
  const [linkedAccount, setLinkedAccount] = useState<LinkedAccount | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [statements, setStatements] = useState<Statement[]>([]);

  const { checkGuard: summaryGuard, resetGuard: resetSummaryGuard } = useAsyncGuard(1000);
  const { checkGuard: statementsGuard, resetGuard: resetStatementsGuard } = useAsyncGuard(1000);

  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const fetchSummary = useCallback(async () => {
    if (!customer_uuid || !summaryGuard()) return;

    show();
    setError(null);
    try {
      const monthYear = `${selectedMonth}-${selectedYear}`;
      const response = await creditService.getSummary(customer_uuid, monthYear);
      setCreditSummary(mapCreditSummaryData(response.data));
      setPeriodSummary(mapPeriodSummary(response.data));
      setLinkedAccount(mapLinkedAccount(response.data));
    } catch (err) {
      setError('No se pudo cargar el resumen de crédito.');
      console.error('Error fetching credit summary:', err);
      resetSummaryGuard();
    } finally {
      hide();
    }
  }, [customer_uuid, selectedMonth, selectedYear, show, hide, summaryGuard, resetSummaryGuard]);

  const handleStatementList = useCallback(async (page: number, limit: number) => {
    if (!customer_uuid || !statementsGuard()) return;

    show();
    try {
      const response = await creditService.getStatements(customer_uuid);
      setStatements(response.data.statements || []);
      setPaginationData((prev) => ({
        ...prev,
        currentPage: page,
        itemsPerPage: limit,
        totalItems: response.data.statements?.length || 0,
        totalPages: Math.ceil((response.data.statements?.length || 0) / limit),
      }));
    } catch (err) {
      console.error('Error fetching statements:', err);
      resetStatementsGuard();
    } finally {
      hide();
    }
  }, [customer_uuid, show, hide, statementsGuard, resetStatementsGuard]);

  useEffect(() => {
    if (customer_uuid) {
      fetchSummary();
      handleStatementList(1, paginationData.itemsPerPage);
    }
  }, [customer_uuid, fetchSummary, handleStatementList, paginationData.itemsPerPage]); // Dependencias actualizadas para asegurar la carga.

  const handleSearch = useCallback(() => {
    resetSummaryGuard();
    fetchSummary();
  }, [fetchSummary, resetSummaryGuard]);

  const handleMakePayment = useCallback(() => {
    // TODO: setIsPaymentModalOpen(true);
    info(t('common.featureComingSoon'));
  }, [info]);

  const handleClosePaymentModal = useCallback(() => {
    if (!isConfirmingPayment) {
      setIsPaymentModalOpen(false);
    }
  }, [isConfirmingPayment]);

  const handleConfirmPayment = useCallback(async () => {
    setIsConfirmingPayment(true);
    // TODO: llamar al servicio de pago
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsConfirmingPayment(false);
    setIsPaymentModalOpen(false);
  }, []);

  const handleDownloadStatement = useCallback((statement: Statement) => {
    if (statement.download_url) {
      window.open(statement.download_url, '_blank');
    }
  }, []);

  const handlePageChange = useCallback((page: number, limit: number) => {
    resetStatementsGuard();
    handleStatementList(page, limit);
  }, [handleStatementList, resetStatementsGuard]);

  const pagination = {
    ...paginationData,
    onPageChange: handlePageChange
  };

  return {
    creditSummary,
    periodSummary,
    linkedAccount,
    isLoading,
    error,
    isPaymentModalOpen,
    isConfirmingPayment,
    pagination,
    handleMakePayment,
    handleClosePaymentModal,
    handleConfirmPayment,
    handleDownloadStatement,
    selectedMonth,
    selectedYear,
    setSelectedMonth,
    setSelectedYear,
    handleSearch,
    statements,
  };
}
